import { Link } from "react-router-dom";
import { Plus, MessagesSquare } from "lucide-react";
import { CommunityRail } from "@/components/rounds/CommunityRail";
import { ThreadRow } from "@/components/rounds/ThreadRow";
import {
  useRoundsThreads,
  type RoundCommunity,
  type RoundsSort,
} from "@/lib/rounds";
import { useState } from "react";
import "@/styles/rounds.css";

const SORTS: Array<{ value: RoundsSort; label: string }> = [
  { value: "hot", label: "Hot" },
  { value: "new", label: "New" },
  { value: "top", label: "Top" },
];

export function RoundsList({
  citySlug,
  communities,
  community,
  title,
  description,
}: {
  citySlug: string;
  communities: RoundCommunity[];
  community?: RoundCommunity;
  title: string;
  description: string;
}) {
  const [sort, setSort] = useState<RoundsSort>("hot");
  const threads = useRoundsThreads(citySlug, community?.slug, sort);
  const items = threads.data?.pages.flatMap((page) => page.threads) ?? [];
  const newThreadHref = community
    ? `/board/rounds/new?community=${encodeURIComponent(community.slug)}`
    : "/board/rounds/new";

  return (
    <div className="pb-rounds-layout">
      <CommunityRail
        citySlug={citySlug}
        communities={communities}
        activeSlug={community?.slug}
      />
      <section className="pb-rounds-list-panel" aria-labelledby="rounds-list-heading">
        <header className="pb-rounds-list-heading">
          <div>
            <p className="pb-board-eyebrow">Clinician discussion</p>
            <h1 id="rounds-list-heading">{title}</h1>
            <p>{description}</p>
            {community ? <Link to="/board/rounds/guidelines">Read the community rules</Link> : null}
          </div>
          <Link className="pb-rounds-primary" to={newThreadHref}>
            <Plus size={15} aria-hidden="true" />New thread
          </Link>
        </header>

        <div className="pb-rounds-sort" aria-label="Sort threads">
          {SORTS.map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={sort === item.value}
              onClick={() => setSort(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {threads.isLoading ? <div className="pb-rounds-state" role="status">Loading discussions…</div> : null}
        {threads.isError ? (
          <div className="pb-rounds-state pb-rounds-state--error" role="alert">
            <span>Discussions could not be loaded.</span>
            <button type="button" onClick={() => void threads.refetch()}>Retry</button>
          </div>
        ) : null}
        {!threads.isLoading && !threads.isError && !items.length ? (
          <div className="pb-rounds-empty">
            <div className="pb-workspace-emblem" aria-hidden="true"><MessagesSquare size={38} strokeWidth={1.4} /></div>
            <p className="pb-board-eyebrow">Better practice, together</p>
            <h2>Start a conversation that matters.</h2>
            <p>No discussions here yet. Share a practical question, compare approaches or exchange insights with your local colleagues.</p>
            <Link className="pb-rounds-primary" to={newThreadHref}><Plus size={16} aria-hidden="true" />Start the first discussion</Link>
            <small>Keep discussions free of patient names and identifying details.</small>
          </div>
        ) : null}
        {items.length ? (
          <div className="pb-rounds-thread-list">
            {items.map((thread) => <ThreadRow key={thread.id} thread={thread} />)}
          </div>
        ) : null}
        {threads.hasNextPage ? (
          <button
            className="pb-rounds-load-more"
            type="button"
            disabled={threads.isFetchingNextPage}
            onClick={() => void threads.fetchNextPage()}
          >
            {threads.isFetchingNextPage ? "Loading…" : "Load more discussions"}
          </button>
        ) : null}
      </section>
    </div>
  );
}
